import type { BreakpointSize } from './types'

/**
 * Reads the Tailwind CSS variable --breakpoint-{size} from :root.
 * Falls back to '0px' if the variable is not defined.
 */
const getBreakpointValue = (size: BreakpointSize): string => {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(`--breakpoint-${size}`)
    .trim()
  // getPropertyValue returns "" (never null/undefined) when the custom property is unset,
  // so `??` would never trigger — use `||` to catch the empty-string case.
  return value || '0px'
}

/**
 * Builds the `MediaQueryList` matching the Tailwind `(max-width: …)` query
 * for the given breakpoint. Shared between `isBelowBreakpoint` (one-shot
 * `.matches` read) and `useResponsive` (subscribed via `addEventListener`).
 */
export const getBreakpointMediaQuery = (size: BreakpointSize): MediaQueryList =>
  globalThis.matchMedia(`(max-width: ${getBreakpointValue(size)})`)

/**
 * Imperative (non-reactive) breakpoint check. Returns true if the viewport
 * is at or below the given Tailwind breakpoint at the moment of the call.
 *
 * Use this in event handlers / one-shot effects where you only need the
 * viewport state at a specific moment. For continuous tracking, use the
 * `useResponsive` hook instead.
 */
export const isBelowBreakpoint = (size: BreakpointSize): boolean =>
  getBreakpointMediaQuery(size).matches
